import React, { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "sonner";
import Pagination from "../Pagination";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import LoadingScreen from "../Utils/LoadingScreen";
import { FileDown, RefreshCcw, Wrench, Eye, Pen } from "lucide-react";

function Services() {
  const { api, adminId } = useContext(GlobalContext);

  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);

  const effectiveAdminId = adminId || sessionAdminId;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editService, setEditService] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  const openDetails = (item) => {
    setSelectedService(item);
    setShowModal(true);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchAllSalesServices = async () => {
    setLoading(true);
    try {
      const payload = { adminId: String(effectiveAdminId) };
      const res = await api.post(`/Admin/v1.0/getAllSalesServices`, payload);
      if (res?.data?.responseCode === "00") {
        setServices(res.data.data || []);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to fetch services");
      }
    } catch {
      toast.error("Something went wrong while fetching services");
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesServices = async () => {
    setLoading(true);

    try {
      const payload = {
        adminId: String(effectiveAdminId),
        userid: "",
      };

      const res = await api.post(`/Admin/v1.0/getSalesServices`, payload);

      if (res?.data?.responseCode === "00") {
        setServices(res.data.data || []);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to fetch services");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const updateServiceStatus = async () => {
    if (!editService) return;

    try {
      const payload = {
        adminId: String(effectiveAdminId),
        userid: "",
        menuid: String(editService.menu_id),
        status: newStatus,
      };

      const res = await api.post(`/Admin/v1.0/updateSalesService`, payload);
      if (res?.data?.responseCode === "00") {
        toast.success("Service status updated successfully!");
        setShowEditModal(false);
        fetchAllSalesServices();
      } else {
        toast.error(res?.data?.responseMessage || "Failed to update");
      }
    } catch (err) {
      toast.error("Something went wrong while updating");
    }
  };

  useEffect(() => {
    fetchAllSalesServices();
  }, []);

  const filteredServices = useMemo(() => {
    if (!search.trim()) return services;
    const s = search.toLowerCase();
    return services.filter((item) =>
      [item.menu_title, item.status, item.indate].some((v) =>
        v?.toLowerCase().includes(s)
      )
    );
  }, [services, search]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredServices.slice(start, start + itemsPerPage);
  }, [filteredServices, currentPage]);

  if (loading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 rounded-2xl shadow">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="aoc-csp-screen w-full bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow min-h-[90svh]">
      <div className="px-4 py-6 md:py-4">
        <div className="flex items-center justify-between gap-4 mb-6 md:mb-10 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-blue-100 rounded-2xl flex items-center justify-center shadow-sm">
              <Wrench className="h-6 w-6 text-blue-500" />
            </div>

            <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 tracking-tight">
              Services
            </h1>
          </div>

          <div className="flex gap-3 flex-wrap items-end">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search service..."
              className="aoc-csp-control-new min-w-[160px]"
            />

            <button className="btn btn-refresh" onClick={fetchAllSalesServices}>
              <RefreshCcw size={14} />
              <span>Refresh</span>
            </button>

            <button
              className="btn btn-export"
              onClick={() =>
                exportAnyJsonToExcel({
                  data: filteredServices,
                  filePrefix: "servicesExport",
                  sheetName: "services",
                })
              }
            >
              <FileDown size={14} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {filteredServices.length === 0 && (
          <p className="text-red-600 text-lg">No Services Found!</p>
        )}

        {filteredServices.length > 0 && (
          <table className="aoc-csp-table w-full overflow-hidden">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">Service Title</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">In Date</th>
                <th className="px-4 py-2 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedRows.map((item, index) => (
                <tr key={item.menu_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td className="px-4 py-2">{item.menu_title}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold 
                    ${
                      item.status === "Y"
                        ? "bg-green-100 text-green-700"
                        : item.status === "N"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    >
                      {item.status === "Y" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-2">{item.indate}</td>
                  <td className="text-center">
                    <div className="flex items-center gap-3">
                      <button
                        className="aoc-csp-icon-btn text-gray-600 hover:text-gray-900"
                        title="View"
                        onClick={() => openDetails(item)}
                      >
                        <Eye className="h-5 w-5" />
                      </button>

                      <button
                        className="aoc-csp-icon-btn text-blue-600 hover:text-blue-800"
                        title="Edit"
                        onClick={() => {
                          setEditService(item);
                          setNewStatus(item.status);
                          setShowEditModal(true);
                        }}
                      >
                        <Pen className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Service Details</h2>
            <p>
              <strong>ID:</strong> {selectedService?.menu_id}
            </p>
            <p>
              <strong>Title:</strong> {selectedService?.menu_title}
            </p>
            <p>
              <strong>Status:</strong>

              {selectedService?.status === "Y" ? (
                <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                  Active
                </span>
              ) : (
                <span className="ml-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                  Inactive
                </span>
              )}
            </p>

            <p className="mt-2">
              <strong>Date:</strong> {selectedService?.indate}
            </p>

            <div className="text-right mt-5">
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Edit Service Status</h2>
            <p>
              <strong>Service:</strong> {editService?.menu_title}
            </p>

            <div className="mt-4">
              <label className="block text-sm mb-1 font-medium">Status</label>

              <select
                className="w-full border p-2 rounded-lg"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="Y">Active</option>
                <option value="N">Inactive</option>
              </select>
            </div>

            <div className="flex justify-end mt-6 gap-3">
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                onClick={updateServiceStatus}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredServices.length > itemsPerPage && (
        <div className="flex justify-center pb-5">
          <Pagination
            totalItems={filteredServices.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}

export default Services;
